// ABOUTME: The page's theme for an echarts chart: on a switch it disposes the chart, so the effect that
// ABOUTME: creates it (with the theme in its deps) draws it afresh in the new theme with the options it has now.

import type { RefObject } from "react"
import { useEffect, useRef } from "react"
import type { ResolvedTheme } from "./theme"
import { useTheme } from "./useTheme"

/** The part of an echarts instance this needs — the full and the tree-shaken (`echarts/core`) builds both fit. */
interface DisposableChart {
  dispose(): void
  isDisposed(): boolean
}

/**
 * Call it before the effect that creates the chart, create the chart with `chartTheme(theme)` and list
 * `theme` in that effect's deps: a switch empties the ref here first, and the effect creates the chart
 * anew. echarts' own `setTheme` is not used on purpose — it rebuilds the model from the options of the
 * first `setOption` (node_modules/echarts/lib/model/Global.js:398-401; a later merge-mode `setOption`
 * never reaches that backup, lib/model/OptionManager.js:97-127), so a chart that merges its updates
 * would fall back to its first data.
 */
export function useChartTheme(chartRef: RefObject<DisposableChart | null> | undefined): ResolvedTheme {
  const { resolved } = useTheme()
  const applied = useRef(resolved)

  useEffect(() => {
    if (applied.current === resolved || !chartRef) return
    applied.current = resolved
    const instance = chartRef.current
    if (instance && !instance.isDisposed()) instance.dispose()
    chartRef.current = null
  }, [chartRef, resolved])

  return resolved
}
